import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import { createStore, del, get, set } from 'idb-keyval';
import { ApiError } from './api';

const DAY = 24 * 60 * 60 * 1000;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Show cached data immediately, refresh in the background when online.
      networkMode: 'offlineFirst',
      staleTime: 30_000,
      gcTime: 7 * DAY,
      retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
    },
    // 'always': offline, a change fails at once with "Ni povezave" instead of waiting silently
    // in memory (and getting lost when the app is closed). Check-ins have their own outbox.
    mutations: { networkMode: 'always' },
  },
});

const store = typeof indexedDB === 'undefined' ? undefined : createStore('planer-cache', 'queries');

export const persister = createAsyncStoragePersister({
  key: 'planer-query-cache',
  storage: {
    getItem: (key) => get<string>(key, store).then((v) => v ?? null),
    setItem: (key, value: string) => set(key, value, store),
    removeItem: (key) => del(key, store),
  },
});

export const PERSIST_MAX_AGE = 7 * DAY;
/** Bump when the shape of cached API data changes. */
export const CACHE_BUSTER = 'v3';

/** Forgets everything cached on this device (logout or expired session). */
export async function clearLocalData(): Promise<void> {
  queryClient.clear();
  try {
    await persister.removeClient();
  } catch {
    // IndexedDB unavailable: nothing persisted
  }
}
